import React from "react";
import { useMatch, useRouter } from "@tanstack/react-location";
import { FaLinkedin } from "react-icons/fa";
import Footer from "../Footer/Footer";

const TeamCard = ({ person, role }) => {
    const borderColor = {
        president: "border-blue-500/30",
        gensec: "border-blue-500/30",
        head: "border-emerald-500/30",
        deputy: "border-purple-500/30",
        core: "border-purple-500/30"
    }[role];

    const ringColor = {
        president: "ring-blue-500/30",
        gensec: "ring-blue-500/30",
        head: "ring-emerald-500/30",
        deputy: "ring-purple-500/30",
        core: "ring-purple-500/30"
    }[role];

    return (
        <div className={`group bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm rounded-xl p-6 border ${borderColor} hover:scale-[1.03] transition-all duration-300 shadow-md`}>
            <div className="flex flex-col items-center text-center space-y-4">
                <div className={`w-24 h-24 rounded-full overflow-hidden ${ringColor} ring-4 transition-all duration-300`}>
                    {person.image?.asset ? (
                        <img
                            src={person.image.asset.url}
                            alt={person.name}
                            className="w-full h-full object-cover object-center"
                            onError={(e) => (e.target.style.display = 'none')}
                        />
                    ) : (
                        <div className="w-full h-full bg-gray-400 flex items-center justify-center text-white text-sm">
                            No Image found
                        </div>
                    )}
                </div>
                <div className="space-y-2">
                    <h4 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors duration-300 tracking-wide">
                        {person.name}
                    </h4>
                    <p className="text-sm text-slate-300">
                        {`${person.year} year, ${person.department} dept`}
                    </p>
                    {person.domain && (
                        <span className="inline-block px-3 py-1 mt-1 bg-white/10 text-white text-xs font-semibold rounded-xl tracking-wide border border-white/20">
                            {person.domain}
                        </span>
                    )}
                    {(person.linkedin || person.lnurl) && (
                        <a
                            href={person.linkedin || person.lnurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-slate-400 hover:text-blue-400 transition-colors duration-300"
                        >
                            <span className="ml-2 font-bold">LinkedIn</span>
                            <FaLinkedin size={24} className="ml-1" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const SectionHeader = ({ title }) => (
    <div className="text-center mb-12">
        <div className="inline-block relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-wider">
                {title}
            </h2>
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        </div>
    </div>
);

const Team = () => {
    const { data } = useMatch();
    const router = useRouter();

    if (router.pending) {
        return (
            <div className="flex items-center justify-center space-x-2 animate-pulse h-screen w-screen">
                <div className="w-40 h-40 border-t-4 border-b-4 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            <div className="pt-24 pb-16 px-6 max-w-7xl mx-auto">
                {data?.presidents?.length > 0 && (
                    <>
                        <SectionHeader title="Presidents" />
                        <div className="flex flex-wrap justify-center gap-6 mb-16">
                            {data.presidents.map((p, i) => (
                                <div key={i} className="w-full sm:w-[20rem]">
                                    <TeamCard person={p} role="president" />
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {data?.gensec?.length > 0 && (
                    <>
                        <SectionHeader title="General Secretary" />
                        <div className="flex flex-col items-center gap-10 mb-16">
                            {data.gensec.map((g, i) => (
                                <div key={i} className="w-full max-w-sm">
                                    <TeamCard person={g} role="gensec" />
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {data?.jointSecretaries?.length > 0 && (
                    <>
                        <SectionHeader title="Joint Secretary" />
                        <div className="flex justify-center mb-20">
                            <div className="w-full max-w-sm">
                                {data.jointSecretaries.map((j, i) => (
                                    <TeamCard key={i} person={j} role="gensec" />
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {data?.domainHeads?.length > 0 && (
                    <>
                        <SectionHeader title="Heads" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
                            {data.domainHeads.map((h, i) => (
                                <TeamCard key={i} person={h} role="head" />
                            ))}
                        </div>
                    </>
                )}

                {data?.deputyHeads?.length > 0 && (
                    <>
                        <SectionHeader title="Deputy Heads" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
                            {data.deputyHeads.map((d, i) => (
                                <TeamCard key={i} person={d} role="deputy" />
                            ))}
                        </div>
                    </>
                )}

                {data?.coreMembers?.length > 0 && (
                    <>
                        <SectionHeader title="Core Members" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
                            {data.coreMembers.map((c, i) => (
                                <TeamCard key={i} person={c} role="core" />
                            ))}
                        </div>
                    </>
                )}

                {(!data?.presidents?.length && !data?.gensec?.length && !data?.domainHeads?.length && !data?.deputyHeads?.length && !data?.coreMembers?.length) && (
                    <div className="text-center text-white text-xl">
                        No team members found
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default Team;
